import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ListTree } from "lucide-react";
import { cn } from "@/lib/utils";

// P23C: TOC threshold counts highlight steps only (chapters are section headers,
// not navigable entries, so they do not inflate the threshold).
export const TOC_MIN_VISIBLE_STEPS = 3;

export interface TourTocStep {
  id: string;
  title: string | null;
  // P23C: chapters render as non-clickable bold section headers.
  stepType: "highlight" | "chapter";
}

export interface TourTableOfContentsProps {
  steps: TourTocStep[];
  currentIndex: number;
  onJump: (index: number) => void;
}

export function TourTableOfContents({
  steps,
  currentIndex,
  onJump,
}: TourTableOfContentsProps) {
  // P23C: TOC visible only when there are enough navigable (highlight) steps.
  const highlightCount = steps.filter((s) => s.stepType !== "chapter").length;
  if (highlightCount < TOC_MIN_VISIBLE_STEPS) return null;

  // Build a running counter for highlight ordinals.
  let highlightOrdinal = 0;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          aria-label="Tour steps"
          className="gap-1.5 text-xs px-2"
          data-testid="button-tour-toc-trigger"
        >
          <ListTree className="h-3.5 w-3.5" />
          Steps
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-64 p-1"
        // G7: Joyride tooltip layer sits at zIndex 100 (see
        // TourManager options). The popover defaults below that so it
        // renders behind the bubble and is unclickable. Lift it above.
        style={{ zIndex: 200 }}
        data-testid="popover-tour-toc"
      >
        <ul className="space-y-0.5">
          {steps.map((step, i) => {
            if (step.stepType === "chapter") {
              return (
                <li key={step.id}>
                  <div
                    className="px-2 py-1.5 text-sm font-semibold text-foreground"
                    data-testid={`text-tour-toc-chapter-${i}`}
                  >
                    {step.title || "Chapter"}
                  </div>
                </li>
              );
            }
            highlightOrdinal += 1;
            const ordinal = highlightOrdinal;
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => onJump(i)}
                  data-testid={`button-tour-toc-step-${i}`}
                  className={cn(
                    "w-full text-left pl-5 pr-2 py-1.5 rounded-md text-sm hover-elevate",
                    i === currentIndex &&
                      "bg-accent text-accent-foreground font-medium",
                  )}
                >
                  <span className="text-muted-foreground mr-2">{ordinal}.</span>
                  {step.title || `Step ${ordinal}`}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
